import { InstallButton } from "../components/InstallButton";
import { useCallback, useEffect, useState } from "react";
import { LuArrowRight, LuChartColumn, LuGift, LuCheck, LuFlame, LuGamepad2, LuGlobe, LuMegaphone, LuPartyPopper, LuPlay, LuSparkles, LuStar, LuStore, LuSwords, LuTarget, LuTrophy, LuUser, LuUsers } from "react-icons/lu";
import { GAME_IDS, SUBJECTS, subjectName } from "../../shared/constants";
import { get, post } from "../api";
import { Avatar } from "../components/Avatar";
import { Confetti, Floaties, Ring, Sparkles, SpinCoin } from "../components/fx";
import { HeroStage } from "../components/HeroStage";
import { GoalCard, GoalPrompt } from "../components/heroes";
import { StreakCard } from "../components/streak";
import { GameTile } from "../components/GameTile";
import { MissionRow } from "../components/goals";
import { GameArt } from "../components/GameArt";
import { HeroScene } from "../components/HeroScene";
import { Coin, Count, ErrorBox, GAME_META, Spinner } from "../components/ui";
import { Link } from "../router";
import { useApp, useLoad } from "../store";

const hello = () => {
  const h = new Date().getHours();
  return h < 5 ? "Hej" : h < 10 ? "Godmorgen" : h < 12 ? "God formiddag" : h < 17 ? "God eftermiddag" : "Godaften";
};

/** the games shown on the front page – the ones with the biggest "wow" first */
const FEATURED = ["verden", "ludo", "yatzy", "taarn", "monopoly", "sudoku"];

const QUICK = [
  { to: "/praemier", name: "Mål", sub: "Hent præmier", icon: LuTarget, cls: "q-gold" },
  { to: "/butik", name: "Shop", sub: "Køb ting", icon: LuStore, cls: "q-pink" },
  { to: "/venner", name: "Venner", sub: "Spil sammen", icon: LuUsers, cls: "q-green" },
  { to: "/rangliste", name: "Rangliste", sub: "Hvem er bedst?", icon: LuChartColumn, cls: "q-violet" },
  { to: "/profil", name: "Profil", sub: "Din figur", icon: LuUser, cls: "q-blue" },
];

const SEEN_KEY = "edu-missions-done";
const readSeen = (): string[] | null => {
  try { const v = localStorage.getItem(SEEN_KEY); return v ? (JSON.parse(v) as string[]) : null; } catch { return null; }
};
const writeSeen = (keys: string[]) => { try { localStorage.setItem(SEEN_KEY, JSON.stringify(keys)); } catch { /* private mode */ } };

export function Dashboard() {
  const { me, reload } = useApp();
  const missions = useLoad(() => get("/api/missions"), []);
  const progress = useLoad(() => get("/api/progress"), []);
  const classes = useLoad(() => get("/api/classes/mine"), []);
  const meta = useLoad(() => get("/api/meta"), []);
  const [cheer, setCheer] = useState<{ title: string; coins: number }[]>([]);
  const [stage3d, setStage3d] = useState(false);
  const onStageMode = useCallback((m: "3d" | "flat") => setStage3d(m === "3d"), []);
  const live = (classes.data?.classes ?? []).filter((c: any) => c.session && c.session.status !== "finished");
  const lp = me!.levelProgress;
  const list: any[] = missions.data?.missions ?? [];
  const doneN = list.filter((m) => m.completed).length;
  const daily = list.filter((m) => m.period === "day");
  const weekly = list.filter((m) => m.period === "week");
  const dailyDone = daily.filter((m) => m.completed).length;
  const active = me!.activeMatch;

  /* a little celebration for missions that were completed since the last visit (nothing is shown the very first time) */
  useEffect(() => {
    if (!missions.data) return;
    const done = list.filter((m) => m.completed);
    const seen = readSeen();
    const fresh = seen ? done.filter((m) => !seen.includes(m.key)) : [];
    if (fresh.length) setCheer(fresh.map((m) => ({ title: m.title, coins: m.reward?.coins ?? 0 })));
    writeSeen(done.map((m) => m.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missions.data]);

  const p = progress.data;
  const answeredBy = new Map<string, any>((p?.subjects ?? []).map((s: any) => [s.subject, s]));
  /* a friendly nudge towards something new: a subject that has not been tried yet, otherwise the one with the fewest answers */
  const nudge = p
    ? [...SUBJECTS].sort((a, b) => (answeredBy.get(a.id)?.answered ?? 0) - (answeredBy.get(b.id)?.answered ?? 0))[0]
    : null;
  const nudgeN = nudge ? (answeredBy.get(nudge.id)?.answered ?? 0) : 0;
  const needLeft = Math.max(0, lp.needed - lp.into);

  return (
    <div className="stack home rise">
      {live.map((c: any) => (
        <div key={c.id} className="live-banner" role="status">
          <span className="lb-ico"><LuMegaphone aria-hidden="true" size={22} /></span>
          <span className="lb-text">Din lærer har startet en klasseseance i <strong>{c.name}</strong>: {GAME_META[c.session.game]?.name}</span>
          <Link to={`/seance/${c.session.id}`} className="btn orange">Deltag nu</Link>
        </div>
      ))}

      {me!.role === "student" && (me!.claimable ?? 0) > 0 && (
        <Link to="/praemier" className="claimbar home" aria-label={`${me!.claimable} præmier er klar til at hente under Mål`}>
          <span className="cb-ico" aria-hidden="true"><LuGift size={34} /></span>
          <span className="cb-text"><b>{me!.claimable === 1 ? "Du har 1 præmie at hente!" : `Du har ${me!.claimable} præmier at hente!`}</b><small>Tryk her og indløs dine mønter</small></span>
          <span className="btn gold cb-btn">Hent <LuArrowRight aria-hidden="true" size={18} /></span>
        </Link>
      )}

      <section className={`home-hero${stage3d ? " stage3d" : ""}`} aria-labelledby="hero-h" data-spot>
        <HeroScene />
        <Sparkles n={26} />
        <Floaties kinds={["coin", "cube", "star", "gem"]} n={7} seed={2} />
        {cheer.length > 0 && <Confetti />}
        <div className="hh-inner">
          <div className="hh-side" role="group" aria-label="Dine tal">
            <div className="hbubble"><span className="hb-ico gold coin3d"><SpinCoin size={30} /></span><span><b><Count to={me!.balance} /></b><small>mønter</small></span></div>
            <div className="hbubble"><span className="hb-ico blue"><LuStar aria-hidden="true" size={22} /></span><span><b>Niveau {me!.level}</b><small>{me!.title ?? "Nybegynder"}</small></span></div>
          </div>

          <div className="hh-center">
            <HeroStage
              equipped={{ ...me!.avatar, frame: undefined }}
              progress={lp.into / Math.max(1, lp.needed)}
              level={me!.level}
              xpLeft={needLeft}
              onMode={onStageMode}
              fallback={
                <div className="hh-ring">
                  <Ring value={lp.into} max={Math.max(1, lp.needed)} size={176} stroke={12} tone="gold" label={`XP til niveau ${me!.level + 1}`}>
                    <span className="hh-ava"><Avatar equipped={{ ...me!.avatar, frame: undefined }} size={146} framing="bust" label={`Din figur: ${me!.username}`} /></span>
                  </Ring>
                  <span className="hh-lv" aria-hidden="true">Lv.{me!.level}</span>
                </div>
              }
            />
            <h1 id="hero-h" className="title-game hh-title">{hello()}, {me!.username}!</h1>
            <p className="hh-sub">{needLeft > 0 ? <>Kun <b>{needLeft} XP</b> til niveau {me!.level + 1}</> : <>Klar til næste niveau</>}</p>

            {active ? (
              <Link to={`/kamp/${active.id}`} className="bigplay">
                <span className="bp-ico"><LuPlay aria-hidden="true" size={34} /></span>
                <span className="bp-text"><b>Fortsæt spillet</b><small>{GAME_META[active.game]?.name} {active.status === "lobby" ? "venter på dig" : "er i gang"}</small></span>
                <LuArrowRight className="bp-arrow" aria-hidden="true" size={30} />
              </Link>
            ) : me!.role === "student" ? (
              <>
                <Link to="/verden" className="bigplay world">
                  <span className="bp-ico"><LuGlobe aria-hidden="true" size={36} /></span>
                  <span className="bp-text"><b>Fri Verden</b><small>Kør, sejl og flyv</small></span>
                  <LuArrowRight className="bp-arrow" aria-hidden="true" size={30} />
                </Link>
                <Link to="/spil" className="altplay"><LuSwords aria-hidden="true" size={18} /> Alle spil</Link>
              </>
            ) : (
              <Link to="/spil" className="bigplay">
                <span className="bp-ico"><LuSwords aria-hidden="true" size={36} /></span>
                <span className="bp-text"><b>Spil nu</b><small>Vælg et spil og lær noget nyt</small></span>
                <LuArrowRight className="bp-arrow" aria-hidden="true" size={30} />
              </Link>
            )}
          </div>

          <div className="hh-side right" role="group" aria-label="Missioner og stribe">
            <div className="hbubble">
              <Ring value={dailyDone} max={Math.max(1, daily.length)} size={50} stroke={6} tone="green" label="Dagens missioner klaret"><b className="hb-frac">{dailyDone}/{daily.length}</b></Ring>
              <span><b>Missioner</b><small>{daily.length && dailyDone === daily.length ? "Alle klaret!" : "i dag"}</small></span>
            </div>
            <div className="hbubble"><span className="hb-ico pink"><LuFlame aria-hidden="true" size={22} /></span><span><b>{me!.dayStreak?.current ?? 0} {me!.dayStreak?.current === 1 ? "dag" : "dage"} i træk</b><small>{me!.dayStreak?.doneToday ? "flot!" : "spil i dag"}</small></span></div>
          </div>
        </div>
      </section>

      {cheer.length > 0 && (
        <div className="cheer" role="status">
          <span className="ch-ico"><LuPartyPopper aria-hidden="true" size={26} /></span>
          <span><b>Flot klaret!</b> {cheer.map((c) => c.title).join(" · ")} – præmien venter på dig under <Link to="/praemier">Mål</Link>.</span>
        </div>
      )}


      {me!.role === "student" && (
        <Link to="/verden" className="worldcta" aria-label="Gå ind i Fri Verden – hovedspillet" data-reveal="zoom">
          <span className="wc-art" aria-hidden="true"><GameArt game="verden" /></span>
          <span className="wc-text">
            <small>Hovedspillet</small>
            <b>Fri Verden</b>
            <span>Kør, sejl og flyv – og køb dit eget hus!</span>
            <span className="wc-chips" aria-hidden="true"><em>🚗 Biler</em><em>⛵ Både</em><em>✈️ Fly</em><em>🚁 Helikoptere</em><em>🏠 Huse</em><em>🏁 Ræs</em><em>🎯 Sport</em></span>
          </span>
          <span className="wc-go"><span>Gå ind</span> <LuArrowRight aria-hidden="true" size={26} /></span>
        </Link>
      )}

      {me!.role === "student" && me!.dayStreak && <StreakCard s={me!.dayStreak} />}

      {me!.role === "student" && (me!.goal ? <GoalCard goal={me!.goal} balance={me!.balance} className="on-home" onClear={() => { void post("/api/shop/goal", { id: null }).then(() => reload()); }} /> : <GoalPrompt />)}

      <nav className="quick" aria-label="Genveje" data-stagger>
        {QUICK.map((q) => (
          <Link key={q.to} to={q.to} className={`qtile ${q.cls}`} data-tilt="7">
            <span className="q-ico"><q.icon aria-hidden="true" size={34} /></span>
            <span className="q-text"><b>{q.name}</b><small>{q.sub}</small></span>
            {q.to === "/praemier" && (me!.claimable ?? 0) > 0 ? <span className="q-badge gift" aria-label={`${me!.claimable} præmier klar til at hente`}><LuGift aria-hidden="true" size={14} /> {me!.claimable}</span>
              : q.to === "/praemier" && daily.length > 0 && dailyDone < daily.length ? <span className="q-badge" aria-label={`${daily.length - dailyDone} missioner mangler i dag`}>{daily.length - dailyDone}</span> : null}
          </Link>
        ))}
      </nav>

      {me!.role === "student" && <InstallButton variant="card" />}

      {p && p.rounds < 3 && (
        <section className="card stack" aria-label="Sådan virker EduBattle">
          <h2 style={{ margin: 0 }}>Sådan kommer du i gang</h2>
          <ol className="steps3">
            <li><b>1</b><span>Tryk på den store gule knap</span></li>
            <li><b>2</b><span>Vælg et spil og svar på spørgsmål</span></li>
            <li><b>3</b><span>Tjen mønter og køb ting i Shop</span></li>
          </ol>
        </section>
      )}

      <section aria-labelledby="g-h">
        <div className="sec-head">
          <h2 id="g-h" className="sec-title"><LuGamepad2 aria-hidden="true" /> Vælg dit spil</h2>
          <Link to="/spil" className="btn ghost small">Se alle {GAME_IDS.length} spil <LuArrowRight aria-hidden="true" size={16} /></Link>
        </div>
        <div className="gtiles" data-n={FEATURED.length} data-stagger>
          {FEATURED.map((g) => <GameTile key={g} game={g} rule={meta.data?.rules?.[g]} />)}
        </div>
      </section>

      <div className="grid cols-2" data-stagger>
        <section className="card" aria-labelledby="m-h">
          <h2 id="m-h" className="sec-title"><LuTrophy aria-hidden="true" /> Missioner</h2>
          {missions.loading && <Spinner />}
          <ErrorBox error={missions.error} retry={missions.reload} />
          <div className="stack">
            {daily.map((m: any) => <MissionRow key={m.key} m={m} />)}
            {!missions.loading && daily.length > 0 && dailyDone === daily.length && <div className="cheer inline"><LuPartyPopper aria-hidden="true" size={22} /> Alle dagens missioner er klaret!</div>}
            {weekly.length > 0 && <h3 className="sec-sub">Denne uge</h3>}
            {weekly.map((m: any) => <MissionRow key={m.key} m={m} />)}
            <Link to="/praemier" className="btn ghost small">Se alle mål <LuArrowRight aria-hidden="true" size={16} /></Link>
          </div>
        </section>

        <section className="card" aria-labelledby="p-h">
          <h2 id="p-h" className="sec-title"><LuChartColumn aria-hidden="true" /> Din fremgang</h2>
          {progress.loading && <Spinner />}
          {p && (
            <>
              <div className="bigstats">
                <div className="bs b1"><LuTarget aria-hidden="true" size={22} /><b><Count to={p.correct} /></b><span>rigtige svar</span></div>
                <div className="bs b2"><LuTrophy aria-hidden="true" size={22} /><b><Count to={p.wins} /></b><span>sejre</span></div>
                <div className="bs b3"><LuGamepad2 aria-hidden="true" size={22} /><b><Count to={p.rounds} /></b><span>spil</span></div>
                <div className="bs b4"><LuFlame aria-hidden="true" size={22} /><b><Count to={p.bestStreak} /></b><span>bedste stribe</span></div>
              </div>
              <h3 style={{ marginTop: 16 }}>Fag</h3>
              <div className="stack" style={{ gap: 10 }}>
                {p.subjects.filter((s: any) => s.answered > 0).map((s: any) => {
                  const info = SUBJECTS.find((x) => x.id === s.subject);
                  return (
                    <div key={s.subject} className="subjbar" style={{ ["--sc" as any]: info?.color }}>
                      <div className="row between"><span className="sb-name">{subjectName(s.subject)}</span><small className="muted">{s.accuracy}%</small></div>
                      <div className="sb-track" role="progressbar" aria-label={`${subjectName(s.subject)}: ${s.accuracy}% rigtige`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={s.accuracy ?? 0}><i style={{ width: `${s.accuracy ?? 0}%` }} /></div>
                    </div>
                  );
                })}
                {!p.subjects.some((s: any) => s.answered > 0) && <p className="muted">Spil dit første spil for at se din fremgang her.</p>}
              </div>
              {nudge && (
                <Link to={`/spil/quizduel?fag=${nudge.id}`} className="nudge">
                  <span className="nu-ico"><LuSparkles aria-hidden="true" size={22} /></span>
                  <span><b>{nudgeN === 0 ? `Prøv ${nudge.name}` : `Træn ${nudge.name}`}</b></span>
                  <LuArrowRight aria-hidden="true" size={20} />
                </Link>
              )}
            </>
          )}
        </section>
      </div>

      {p?.recent?.length > 0 && (
        <section className="card" aria-labelledby="r-h">
          <h2 id="r-h" className="sec-title">Seneste spil</h2>
          <ul className="recent">
            {p.recent.slice(0, 6).map((r: any, i: number) => (
              <li key={i} className={`out-${r.outcome}`}>
                <span className="rc-game"><span className="rc-art" aria-hidden="true"><GameArt game={r.game} /></span>{GAME_META[r.game]?.name}</span>
                <span className="rc-out">{{ win: "Sejr", loss: "Nederlag", draw: "Uafgjort", none: "Øvet" }[r.outcome as "win"] ?? "—"}</span>
                <span className="rc-num"><Coin /> {r.coins}</span>
                <span className="rc-num">+{r.xp} XP</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
